/**
 * Parses an axios error from the super-admin backend into a shape the UI can use
 * directly: a banner-level message (for 401/403/429/5xx) OR field-level messages
 * (for 422 validation). Shared by CompaniesListPage, CreateCompanyPage, and
 * CompanyDetailPage. LoginPage has a local copy — intentionally not refactoring
 * it to avoid disturbing the stable auth flow.
 */
export interface SuperAdminErrorShape {
    banner: string | null;
    fields: Record<string, string>;
}

export function parseSuperAdminError(err: any): SuperAdminErrorShape {
    const status = err?.response?.status;
    const data = err?.response?.data;

    if (status === 422 && data?.errors && typeof data.errors === 'object') {
        const fields: Record<string, string> = {};
        for (const k in data.errors) {
            if (Object.prototype.hasOwnProperty.call(data.errors, k)) {
                const v = data.errors[k];
                fields[k] = Array.isArray(v) ? String(v[0]) : String(v);
            }
        }
        return { banner: null, fields };
    }

    // Laravel ValidationExceptions are rendered by app/Exceptions/Handler.php as
    // {error:{code:'validation.failed', fields:{'admin.username':[{code,message}]}}}
    // — no top-level `errors` key — so without this branch a validation failure
    // (e.g. the globally-unique admin.username on create-company) fell through to
    // the generic "Something went wrong" banner with no field highlighted.
    if (status === 422 && data?.error?.fields && typeof data.error.fields === 'object') {
        const fields: Record<string, string> = {};
        for (const k in data.error.fields) {
            if (Object.prototype.hasOwnProperty.call(data.error.fields, k)) {
                const v = data.error.fields[k];
                const first = Array.isArray(v) ? v[0] : v;
                fields[k] = String(first?.message || first?.params?.message || 'Invalid value.');
            }
        }
        if (Object.keys(fields).length > 0) {
            return { banner: null, fields };
        }
    }

    if (status === 429) {
        return {
            banner: 'Too many attempts. Please wait a few minutes and try again.',
            fields: {},
        };
    }

    if (status === 409) {
        return {
            banner: data?.message || 'Conflict: the resource is not in a state that allows this action.',
            fields: {},
        };
    }

    const msg = data?.message || data?.error?.params?.message;
    if (status === 401 || status === 403) {
        return { banner: msg || 'Not authorized.', fields: {} };
    }

    return { banner: msg || 'Something went wrong. Please try again.', fields: {} };
}
